'use client'

import Header from '@/app/[locale]/(home)/_components/Header'
import Breadcrumbs from '@/_components/Breadcrumbs'
import BreadcrumbJsonLd from '@/_components/Breadcrumbs/BreadcrumbJsonLd'
import { buildCatalogBreadcrumbs } from '@/_components/Breadcrumbs/buildCatalogBreadcrumbs'
import { getBrandHref } from '@/_lib/brand-href'
import { IShipper } from '@/_types'
import type { ICategoryWithProducts } from '@/features/brand-menu'
import {
  buildMenuItems,
  buildSections,
  type Section,
} from '@/features/brand-menu'
import { Box, Container, Grid } from '@chakra-ui/react'
import { useLocale, useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BrandTabs from '../../BrandTabs'
import Sidebar from '../components/Sidebar'
import CartPage from '../components/cart'
import MainContent from './main-content'
import { Alert } from '@/shared/ui/alert'
import { onOpenMap } from '@/_store/common/common.slice'
import { sessionActions } from '@/_store/session/session.slice'
import { useDispatch, useSelector } from 'react-redux'
import { IRedux } from '@/_store/types'
import { Button } from '@/components/ui/button'

interface BrandSingleDesktopProps {
  brand: IShipper
  brandLogo?: string | null
  categories?: ICategoryWithProducts[]
  zoneError: boolean
}

const BrandSingleDesktop: React.FC<BrandSingleDesktopProps> = ({
  brand,
  brandLogo,
  categories = [],
  zoneError,
}) => {
  const [activeSection, setActiveSection] = useState<string | null>(null)

  const { data } = useSelector((state: IRedux) => state.settings)
  const { isMapModal } = useSelector((state: IRedux) => state.common)
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const dispatch = useDispatch()
  const t = useTranslations()
  const locale = useLocale()

  const sections = useMemo<Section[]>(
    () => buildSections(categories, locale),
    [categories, locale],
  )

  const menuItems = useMemo(
    () => buildMenuItems(categories, sections, locale),
    [categories, sections, locale],
  )

  // Intersection Observer for active sections
  useEffect(() => {
    if (!sections.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.getAttribute('data-section-id'))
          }
        })
      },
      {
        rootMargin: '-100px 0px -70% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    )

    sections.forEach((section) => {
      const node = sectionRefs.current[section.id]
      if (node) observer.observe(node)
    })

    return () => observer.disconnect()
  }, [sections])

  // Scroll to category when entering page with hash (e.g. /brand/yaponamama#category-slug)
  useEffect(() => {
    if (!sections.length) return
    const hash =
      typeof window !== 'undefined' ? window.location.hash.slice(1) : ''
    if (!hash) return
    const section = sections.find((s) => s.slug === hash)
    if (!section) return
    const el = sectionRefs.current[section.id] ?? document.getElementById(hash)
    if (el) {
      const headerOffset = 125
      const elementPosition = el.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      const timeoutId = window.setTimeout(() => {
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth',
        })
      }, 100)
      return () => window.clearTimeout(timeoutId)
    }
  }, [sections])

  const handleMenuClick = useCallback((sectionId: string) => {
    setActiveSection(sectionId)
    const sectionElement = sectionRefs.current[sectionId]
    if (sectionElement) {
      const headerOffset = 125 // Header height
      const elementPosition = sectionElement.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }, [])

  const handleTakeawayClick = () => {
    const activeOrderType = data?.data?.order_types?.find(
      (orderType) => orderType.type === 'self-pickup',
    )
    if (activeOrderType) {
      dispatch(sessionActions.saveOrderType(activeOrderType))
      if (!isMapModal) {
        dispatch(onOpenMap())
      }
    }
  }

  const breadcrumbData = useMemo(
    () =>
      buildCatalogBreadcrumbs({
        homeLabel: t('home'),
        brand: { name: brand.name, id: brand.id },
      }),
    [t, brand.name, brand.id],
  )

  return (
    <Box bg='background' minH={'100dvh'} pb={10}>
      <Header brandLogo={brandLogo || null} />
      <Container px={10}>
        <BrandTabs activeBrandId={brand.id} />
        <Breadcrumbs data={breadcrumbData} lastAsH1 />
        <BreadcrumbJsonLd
          data={breadcrumbData}
          currentHref={getBrandHref(brand.id)}
        />
        <Grid gap={5} templateColumns={{ base: '1fr', lg: '250px 1fr 328px' }}>
          {/* Left Sidebar */}
          <Sidebar
            shipper={brand}
            zoneError={zoneError}
            menuItems={menuItems}
            activeSection={activeSection}
            onMenuClick={handleMenuClick}
          />

          <Box pr={{ lg: 4 }}>
            {/* Center - Main Content */}
            <MainContent
              brand={brand}
              sections={sections}
              sectionRefs={sectionRefs}
            />
          </Box>

          {/* Right Sidebar - Cart */}
          <Box
            as='aside'
            position='sticky'
            top='125px'
            alignSelf='start'
            display={{ base: 'none', lg: 'block' }}
          >
            {zoneError ? (
              <Box mb={5}>
                <Alert
                  status='error'
                  title={t('address_not_in_service_area')}
                  rounded='2xl'
                >
                  <Button mt={3} onClick={handleTakeawayClick}>
                    {t('order_for_pickup')}
                  </Button>
                </Alert>
              </Box>
            ) : (
              <CartPage />
            )}
          </Box>
        </Grid>
      </Container>
    </Box>
  )
}

export default BrandSingleDesktop
